/**
 * Dev Portal API
 *
 * API functions for developer portal operations:
 * - List all businesses (dev-only via backend)
 * - Inspect any business's subscription
 * - Set limit and feature overrides on any business
 * - Approve / reject subscription requests
 */

import { apiClient } from '@/shared/lib/api-client';
import type {
  BusinessSummary,
  SetLimitOverrideRequest,
  SetFeatureOverrideRequest,
  ClearLimitOverrideRequest,
  ClearFeatureOverrideRequest,
  ClearAllOverridesRequest,
  BusinessActivity,
  SendReminderResponse,
} from '../types/dev.types';
import type { UsageSummary } from '@/features/billing/types/billing.types';
import type { SubscriptionEvent } from '@/features/billing/types/billing.types';

interface BackendBusiness {
  _id: string;
  name: string;
  email: string;
  address: string;
  phoneNumber: string;
  currency: string;
  timezone: string;
  createdAt?: string;
}

function mapBusiness(b: BackendBusiness): BusinessSummary {
  return {
    id: b._id,
    name: b.name,
    email: b.email ?? '',
    address: b.address ?? '',
    phoneNumber: b.phoneNumber ?? '',
    currency: b.currency ?? 'USD',
    timezone: b.timezone ?? 'UTC',
    createdAt: b.createdAt ?? '',
  };
}

/** Fetch all businesses (dev sees all; optional search filter) */
export async function getAllBusinesses(
  search?: string
): Promise<BusinessSummary[]> {
  const query = search ? `?search=${encodeURIComponent(search)}` : '';
  const data = await apiClient.get<BackendBusiness[]>(`/business${query}`);
  return (data ?? []).map(mapBusiness);
}

/** Inspect a specific business's subscription + usage */
export async function inspectSubscription(
  businessId: string
): Promise<UsageSummary> {
  return apiClient.get<UsageSummary>(`/subscription/inspect/${businessId}`);
}

// ─── Overrides (dev-only emergency tools) ───────────────────

/** Set a limit override on a specific business */
export async function setLimitOverride(
  req: SetLimitOverrideRequest
): Promise<UsageSummary> {
  return apiClient.post<UsageSummary>('/subscription/override/limit', req);
}

/** Set a feature override on a specific business */
export async function setFeatureOverride(
  req: SetFeatureOverrideRequest
): Promise<UsageSummary> {
  return apiClient.post<UsageSummary>('/subscription/override/feature', req);
}

/** Clear a limit override on a specific business */
export async function clearLimitOverride(
  req: ClearLimitOverrideRequest
): Promise<UsageSummary> {
  return apiClient.post<UsageSummary>(
    '/subscription/override/clear-limit',
    req
  );
}

/** Clear a feature override on a specific business */
export async function clearFeatureOverride(
  req: ClearFeatureOverrideRequest
): Promise<UsageSummary> {
  return apiClient.post<UsageSummary>(
    '/subscription/override/clear-feature',
    req
  );
}

/** Clear ALL overrides on a specific business */
export async function clearAllOverrides(
  req: ClearAllOverridesRequest
): Promise<UsageSummary> {
  return apiClient.post<UsageSummary>('/subscription/override/clear-all', req);
}

// ─── Activity ───────────────────────────────────────────────

/** Fetch business activity overview (dev only) */
export async function getBusinessActivity(): Promise<BusinessActivity[]> {
  return apiClient.get<BusinessActivity[]>('/business/activity');
}

/** Send a reminder email to a business owner (dev only) */
export async function sendReminderEmail(
  businessId: string
): Promise<SendReminderResponse> {
  return apiClient.post<SendReminderResponse>('/business/send-reminder', {
    businessId,
  });
}

// ─── Subscription Requests (manual payment flow) ────────────────────────

export interface SubscriptionRequestItem {
  _id: string;
  business: string;
  requesterUserId: string;
  requesterName: string;
  plan: string;
  cycle: 'monthly' | 'annual';
  currency: string;
  requestType: 'subscribe' | 'upgrade' | 'addon';
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  requestedAt: string;
  reviewedAt?: string;
  reviewNote?: string;
  message?: string;
  requestedLimits?: {
    storehouses?: number;
    users?: number;
    items?: number;
    monthlyTransactions?: number;
  };
  estimatedMonthlyPrice?: number;
  estimatedAnnualPrice?: number;
}

export type RequestStatusFilter =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'cancelled'
  | 'all';

/** List subscription requests */
export async function listSubscriptionRequests(
  status: RequestStatusFilter = 'pending'
): Promise<SubscriptionRequestItem[]> {
  const data = await apiClient.get<{ requests: SubscriptionRequestItem[] }>(
    `/dev/requests?status=${status}`
  );
  return data.requests ?? [];
}

/** Approve a pending subscription request */
export async function approveSubscriptionRequest(
  id: string,
  note?: string,
  approvedLimits?: {
    storehouses?: number;
    users?: number;
    items?: number;
    monthlyTransactions?: number;
  }
): Promise<{ approved: boolean }> {
  return apiClient.post<{ approved: boolean }>(`/dev/requests/${id}/approve`, {
    note,
    ...(approvedLimits ? { approvedLimits } : {}),
  });
}

/** Reject a pending subscription request */
export async function rejectSubscriptionRequest(
  id: string,
  note?: string
): Promise<{ rejected: boolean }> {
  return apiClient.post<{ rejected: boolean }>(`/dev/requests/${id}/reject`, {
    note,
  });
}

/** Get subscription event history for any business (dev only) */
export async function getBusinessSubscriptionEvents(
  businessId: string
): Promise<SubscriptionEvent[]> {
  const data = await apiClient.get<{ events: SubscriptionEvent[] }>(
    `/dev/subscription-events/${businessId}`
  );
  return data.events ?? [];
}
